import { Component } from "react";

/**
 * Top-level ErrorBoundary — prevents a single component crash from blanking
 * the entire app. Renders a friendly "Try again" panel and logs the error.
 *
 * Place this AS THE OUTERMOST wrapper inside App so it catches everything,
 * including Landing, IndexRouter, and Home.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error to the console for support diagnosis.
    // eslint-disable-next-line no-console
    console.error("[Habano] uncaught render error", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  hardReset = async () => {
    try {
      // Unregister all service workers and clear caches — escape hatch for a
      // stuck stale-bundle situation. Then reload from network.
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* ignore */ }
    try { window.location.replace(window.location.pathname); } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Something went wrong");
    return (
      <main
        data-testid="error-boundary"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#171717" }}
      >
        <div className="max-w-md w-full text-center">
          <div className="eyebrow text-gold">Habano · Hiccup</div>
          <h1 className="font-display text-3xl mt-3 text-foreground">
            Something didn't load right
          </h1>
          <p className="mt-3 text-sm text-foreground/65 leading-relaxed">
            We hit an unexpected snag while loading this page. Try reloading — if it keeps happening, tap the second button to refresh the offline cache.
          </p>
          <pre className="mt-4 text-[10px] text-foreground/40 overflow-hidden whitespace-pre-wrap break-words max-h-24">
            {msg}
          </pre>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              data-testid="error-boundary-reload"
              onClick={() => window.location.reload()}
              className="px-5 py-3 bg-gold text-[hsl(var(--primary-foreground))] hover:opacity-90 text-xs uppercase tracking-widest"
              style={{ minHeight: 48 }}
            >
              Reload the app
            </button>
            <button
              type="button"
              data-testid="error-boundary-hard-reset"
              onClick={this.hardReset}
              className="px-5 py-3 border border-gold-strong text-gold hover:bg-[rgba(232,184,75,0.08)] text-xs uppercase tracking-widest"
              style={{ minHeight: 48 }}
            >
              Clear cache &amp; reload
            </button>
          </div>
        </div>
      </main>
    );
  }
}
